/**
 * useFeatureFlag — read-only hook for client-side feature toggles.
 *
 * Resolution order (first match wins):
 *   1. URL query string: `?<flag>=1` enables, `?<flag>=0` disables
 *   2. localStorage: key `feature.<flag>` set to '1' or '0'
 *   3. Default (passed as 2nd arg, defaults to false)
 *
 * Setting via URL also persists to localStorage so the flag sticks
 * across navigations on the same browser. Persistence is best-
 * effort; if storage is blocked the URL still works for a session.
 *
 * Used so far:
 *   - tiptap (Editor route) — Tiptap-based free-flow document
 *     editor. Default ON since Phase 6; legacy EditableStructuredPreview
 *     stays available as a fallback via ?tiptap=0 during the pilot.
 */
import { useEffect, useState } from 'react';

const STORAGE_PREFIX = 'feature.';

function readStorage(flag: string): boolean | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + flag);
    if (raw === '1') return true;
    if (raw === '0') return false;
    return null;
  } catch {
    return null;
  }
}

function writeStorage(flag: string, on: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + flag, on ? '1' : '0');
  } catch {
    /* ignore — best-effort persistence */
  }
}

function readUrl(flag: string): boolean | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get(flag);
  if (raw === '1' || raw === 'true') return true;
  if (raw === '0' || raw === 'false') return false;
  return null;
}

function resolve(flag: string, defaultValue: boolean): boolean {
  const urlValue = readUrl(flag);
  if (urlValue !== null) {
    // URL writes through to storage so the flag persists.
    writeStorage(flag, urlValue);
    return urlValue;
  }
  const stored = readStorage(flag);
  if (stored !== null) return stored;
  return defaultValue;
}

export function useFeatureFlag(flag: string, defaultValue = false): boolean {
  const [value, setValue] = useState<boolean>(() => resolve(flag, defaultValue));
  useEffect(() => {
    // Re-read when the URL search changes (browser back/forward) so
    // toggling via ?flag= takes effect without a page reload.
    const onPopState = () => setValue(resolve(flag, defaultValue));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [flag, defaultValue]);
  return value;
}
